import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Centered bar header.
 *
 * Handles the mobile menu panel (outside click, escape, close animation), the
 * desktop submenu flyouts on keyboard/touch, the sticky/overlay scroll state,
 * and keeps the header height custom properties up to date for the rest of
 * the theme.
 */
class BarHeader extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  #menu = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /** @type {number | null} */
  #scrollRafId = null;

  #closeTimeout = 0;

  connectedCallback() {
    this.#menu = this.querySelector('.bar-header__menu');

    document.addEventListener('click', this.#handleDocumentClick, true);
    document.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('click', this.#handleInsideClick);

    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#updateScrollState();

    this.#updateHeights();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateHeights());
      this.#resizeObserver.observe(this);
    }

    window.addEventListener('resize', this.#updateHeights);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick, true);
    document.removeEventListener('keydown', this.#handleKeydown);
    window.removeEventListener('resize', this.#updateHeights);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollContainer = null;
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#closeTimeout);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
  }

  /** Closes the mobile panel and any open desktop flyout when a click lands outside of them. */
  #handleDocumentClick = (/** @type {MouseEvent} */ event) => {
    const target = event.composedPath?.()[0] ?? event.target;

    if (this.#menu?.open && !(target instanceof Node && this.#menu.contains(target))) {
      this.closeMenu();
    }

    for (const item of this.#openFlyouts()) {
      if (target instanceof Node && item.contains(target)) continue;
      item.classList.remove('is-open');
    }
  };

  /** Toggles desktop flyouts from their caret button and closes the panel after following a link. */
  #handleInsideClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('.bar-header__nav-toggle');
    if (toggle) {
      event.preventDefault();
      const item = toggle.closest('.bar-header__nav-item');
      if (!(item instanceof HTMLElement)) return;

      const willOpen = !item.classList.contains('is-open');
      for (const other of this.#openFlyouts()) {
        if (other !== item) other.classList.remove('is-open');
      }
      item.classList.toggle('is-open', willOpen);
      toggle.setAttribute('aria-expanded', String(willOpen));
      return;
    }

    if (this.#menu?.open && target.closest('a[href]') && this.#menu.contains(target)) {
      this.closeMenu({ immediate: true });
    }
  };

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;

    if (this.#menu?.open) {
      this.closeMenu();
      this.#menu.querySelector('summary')?.focus();
      return;
    }

    for (const item of this.#openFlyouts()) {
      item.classList.remove('is-open');
      item.querySelector('.bar-header__nav-toggle')?.setAttribute('aria-expanded', 'false');
      /** @type {HTMLElement | null} */ (item.querySelector('.bar-header__nav-toggle'))?.focus();
    }
  };

  /** @returns {HTMLElement[]} */
  #openFlyouts() {
    return Array.from(this.querySelectorAll('.bar-header__nav-item.is-open'));
  }

  /**
   * Closes the mobile menu panel, playing the close animation unless told otherwise.
   * @param {{ immediate?: boolean }} [options]
   */
  closeMenu({ immediate = false } = {}) {
    const menu = this.#menu;
    if (!menu?.open) return;

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      menu.open = false;
      return;
    }

    menu.classList.add('is-closing');
    clearTimeout(this.#closeTimeout);
    this.#closeTimeout = window.setTimeout(() => {
      menu.classList.remove('is-closing');
      menu.open = false;
    }, 140);
  }

  /** Subscribes to whichever element is currently doing the page scrolling. */
  #bindScroll() {
    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  /**
   * Rebinds the scroll listener when the viewport crosses the squeeze
   * breakpoint (990px), where the scroll container switches between
   * `.page-wrapper` (desktop) and the document root (mobile).
   */
  #handleBreakpointChange = () => {
    this.#scrollContainer?.removeEventListener('scroll', this.#handleScroll);
    this.#bindScroll();
    this.#updateScrollState();
  };

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * Flags the bar once the page has scrolled (drop shadow, solid background)
   * and, for the overlay-sticky behaviour, pins it to the viewport as soon as
   * its natural position reaches the top — so it behaves like a sticky bar
   * rather than scrolling away and dropping back in.
   */
  #updateScrollState = () => {
    const scrollTop = getScrollTop();

    this.classList.toggle('is-scrolled', scrollTop > 4);

    if (this.dataset.behavior !== 'overlay_sticky') return;

    // The section wrapper has zero height and stays in flow, so its viewport
    // offset is where the bar would naturally sit.
    const anchor = this.parentElement ?? this;
    const isStuck = anchor.getBoundingClientRect().top <= 0;
    if (isStuck === this.classList.contains('is-stuck')) return;

    this.classList.toggle('is-stuck', isStuck);
    if (!isStuck) this.closeMenu({ immediate: true });
  };

  /**
   * Mirrors the theme's header height custom properties so sections that offset
   * themselves against the header keep working.
   */
  #updateHeights = () => {
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup) return;

    const headerHeight = this.offsetHeight;
    let groupHeight = 0;

    for (const child of headerGroup.children) {
      if (!(child instanceof HTMLElement) || child.contains(this)) continue;
      groupHeight += child.offsetHeight;
    }

    const overlays = this.dataset.behavior === 'overlay' || this.dataset.behavior === 'overlay_sticky';

    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    document.body.style.setProperty('--transparent-header-offset-boolean', overlays ? '1' : '0');
  };
}

if (!customElements.get('bar-header')) {
  customElements.define('bar-header', BarHeader);
}
